/**
 * RpcBinaryCodec - binary encode/decode for RPC wire payloads.
 *
 * ## Payload layout
 *
 * ```
 * [uint8:  flags — hasNetSubId(bit0)]
 * [uint8:  objectId byte length]
 * [utf-8   objectId bytes]
 * [uint8:  methodName byte length]
 * [utf-8   methodName bytes]
 * [if hasNetSubId: uint8 len + utf-8 bytes]
 * [args section: sequential typed values per NetPropertyMetadata[]]
 * ```
 *
 * Args are encoded sequentially without a bitmask (all args are always present
 * in a valid RPC call). Encoding follows the same type rules as replication
 * (`ReplicationSerializer`), always with forceAll=true.
 */

import { Actor } from '../actors/Actor.js';

import { decodeObjectFromBinary, isActorRef, serializeObjectAndEncode } from './ObjectSerializer.js';
import {
  BinaryReader,
  BinaryWriter,
  readClassDiscriminator,
  writeClassDiscriminator,
} from './ReplicationBinaryCodec.js';

import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

const FLAG_HAS_NET_SUB_ID = 0x01;

// ---------------------------------------------------------------------------
// Payload encoding
// ---------------------------------------------------------------------------

/**
 * Encode a full RPC binary payload from a pre-encoded args section.
 *
 * @param objectId    NetObjectId of the target actor (`ClassName'UUID'`).
 * @param methodName  Name of the RPC method.
 * @param argsPayload Binary args produced by {@link encodeRpcArgs}.
 * @param netSubId    Optional component sub-identifier for component RPC routing.
 */
export function encodeRpcPayload(
  objectId: string,
  methodName: string,
  argsPayload: Uint8Array,
  netSubId?: string,
): Uint8Array {
  const headerWriter = new BinaryWriter();
  const flags = netSubId ? FLAG_HAS_NET_SUB_ID : 0;
  headerWriter.writeUint8(flags);
  writeShortString(headerWriter, objectId);
  writeShortString(headerWriter, methodName);
  if (netSubId) writeShortString(headerWriter, netSubId);
  const header = headerWriter.getBytes();
  const result = new Uint8Array(header.length + argsPayload.length);
  result.set(header);
  result.set(argsPayload, header.length);
  return result;
}

/**
 * Decode the header of an RPC binary payload.
 *
 * @returns Parsed header fields plus a {@link BinaryReader} positioned at the
 *          start of the args section. Call {@link BinaryReader.remainingBytes}
 *          to get the args slice for later decoding.
 */
export function decodeRpcHeader(payload: Uint8Array): {
  objectId: string;
  methodName: string;
  netSubId: string | undefined;
  reader: BinaryReader;
} {
  const reader = new BinaryReader(payload);
  const flags = reader.readUint8();
  const objectId = readShortString(reader);
  const methodName = readShortString(reader);
  const netSubId = (flags & FLAG_HAS_NET_SUB_ID) ? readShortString(reader) : undefined;
  return { objectId, methodName, netSubId, reader };
}

// ---------------------------------------------------------------------------
// Args encoding
// ---------------------------------------------------------------------------

/**
 * Encode RPC arguments to binary using typed `NetPropertyMetadata`.
 * Each arg is encoded sequentially (forceAll, no bitmask).
 *
 * @param args      Argument values in declaration order.
 * @param paramMeta Matching `NetPropertyMetadata` for each parameter.
 */
export function encodeRpcArgs(args: unknown[], paramMeta: NetPropertyMetadata[]): Uint8Array {
  const writer = new BinaryWriter();
  for (let i = 0; i < paramMeta.length; i++) {
    encodeRpcValue(writer, args[i], paramMeta[i]);
  }
  return writer.getBytes();
}

/**
 * Decode RPC arguments from the reader using typed `NetPropertyMetadata`.
 *
 * @param reader    Reader positioned at the start of the args section.
 * @param paramMeta Matching `NetPropertyMetadata` for each parameter.
 */
export function decodeRpcArgs(reader: BinaryReader, paramMeta: NetPropertyMetadata[]): unknown[] {
  const args: unknown[] = [];
  for (const net of paramMeta) {
    args.push(decodeRpcValue(reader, null, net));
  }
  return args;
}

// ---------------------------------------------------------------------------
// Per-value encode
// ---------------------------------------------------------------------------

function encodeRpcValue(writer: BinaryWriter, value: unknown, net: NetPropertyMetadata): void {
  // Nullable null — write presence=false
  if (net.nullable && (value === null || value === undefined)) {
    writer.writePresence(false);
    return;
  }

  switch (net.netType) {
    case 'bool':
      if (net.nullable) writer.writePresence(true);
      writer.writeBool(value as boolean);
      break;

    case 'uint8':
    case 'int8':
    case 'uint16':
    case 'int16':
    case 'int32':
    case 'uint32':
    case 'float32':
    case 'float64': {
      const v = typeof value === 'number' ? value : 0;
      if (net.nullable) writer.writePresence(true);
      switch (net.netType) {
        case 'uint8': writer.writeUint8(v); break;
        case 'int8': writer.writeInt8(v); break;
        case 'uint16': writer.writeUint16(v); break;
        case 'int16': writer.writeInt16(v); break;
        case 'int32': writer.writeInt32(v); break;
        case 'uint32': writer.writeUint32(v); break;
        case 'float32': writer.writeFloat32(v); break;
        case 'float64': writer.writeFloat64(v); break;
      }
      break;
    }

    case 'string':
      if (net.nullable) writer.writePresence(true);
      writer.writeString(typeof value === 'string' ? value : '');
      break;

    case 'enum': {
      const opts = net.enumOptions ?? [];
      const idx = opts.indexOf(value as string);
      if (net.nullable) writer.writePresence(true);
      writer.writeUint8(idx >= 0 ? idx : 0);
      break;
    }

    case 'actorRef': {
      const uuid = value instanceof Actor ? value.uuid : (isActorRef(value) ? value.$actorRef : '');
      if (net.nullable) writer.writePresence(true);
      writer.writeActorId(uuid);
      break;
    }

    case 'vector2': {
      const v = value as { x: number; y: number };
      if (net.nullable) writer.writePresence(true);
      writer.writeVector2(v);
      break;
    }

    case 'vector3': {
      const v = value as { x: number; y: number; z: number };
      if (net.nullable) writer.writePresence(true);
      writer.writeVector3(v);
      break;
    }

    case 'euler': {
      const v = value as { x: number; y: number; z: number };
      if (net.nullable) writer.writePresence(true);
      writer.writeEuler(v);
      break;
    }

    case 'vector4':
    case 'quaternion': {
      const v = value as { x: number; y: number; z: number; w: number };
      if (net.nullable) writer.writePresence(true);
      writer.writeVector4(v);
      break;
    }

    case 'struct':
    case 'class': {
      if (value === null || value === undefined) {
        if (net.nullable) {
          writer.writePresence(false);
        } else if (net.netType === 'class' && net.polymorphic) {
          writer.writeUint32(0); // null hash
        }
        return;
      }
      if (net.nullable) writer.writePresence(true);
      if (net.netType === 'class' && net.polymorphic) writeClassDiscriminator(writer, value as object);
      // forceAll (lastState=null) — every field is written for RPC params
      serializeObjectAndEncode(writer, value as object, null, {});
      break;
    }

    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      if (net.nullable) writer.writePresence(true);
      writer.writeUint16(arr.length);
      if (net.element) {
        for (const elem of arr) {
          encodeRpcValue(writer, elem, net.element);
        }
      }
      break;
    }

    default:
      console.warn(`[RpcBinaryCodec] Unknown netType during encode: ${(net as NetPropertyMetadata).netType}`);
  }
}

// ---------------------------------------------------------------------------
// Per-value decode
// ---------------------------------------------------------------------------

function decodeRpcValue(reader: BinaryReader, currentValue: unknown, net: NetPropertyMetadata): unknown {
  if (net.nullable && !reader.readPresence()) {
    return null;
  }

  switch (net.netType) {
    case 'bool': return reader.readBool();
    case 'uint8': return reader.readUint8();
    case 'int8': return reader.readInt8();
    case 'uint16': return reader.readUint16();
    case 'int16': return reader.readInt16();
    case 'int32': return reader.readInt32();
    case 'uint32': return reader.readUint32();
    case 'float32': return reader.readFloat32();
    case 'float64': return reader.readFloat64();
    case 'string': return reader.readString();

    case 'enum': {
      const idx = reader.readUint8();
      const opts = net.enumOptions ?? [];
      return opts[idx] ?? opts[0] ?? '';
    }

    case 'actorRef': {
      const uuid = reader.readActorId();
      return uuid || null;
    }

    case 'vector2': {
      const raw = reader.readVector2();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y]);
        return currentValue;
      }
      return raw;
    }

    case 'vector3': {
      const raw = reader.readVector3();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
        return currentValue;
      }
      return raw;
    }

    case 'euler': {
      const raw = reader.readEuler();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
        return currentValue;
      }
      return raw;
    }

    case 'vector4':
    case 'quaternion': {
      const raw = reader.readVector4();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z, raw.w]);
        return currentValue;
      }
      return raw;
    }

    case 'struct': {
      let instance = currentValue;
      if ((instance === null || instance === undefined) && net.structClass) {
        instance = new (net.structClass as new () => object)();
      }
      if (instance === null || instance === undefined) {
        throw new Error('[RpcBinaryCodec] Cannot decode struct param: no current instance and no structClass.');
      }
      decodeObjectFromBinary(reader, instance as object, undefined, '', new Map());
      return instance;
    }

    case 'class': {
      let ctor: (new (...args: any[]) => any) | null = null;
      if (net.polymorphic) {
        ctor = readClassDiscriminator(reader);
      }
      let instance = currentValue;
      if (ctor && (instance === null || instance === undefined || !(instance instanceof ctor))) {
        instance = new ctor();
      }
      if (instance === null || instance === undefined) {
        throw new Error('[RpcBinaryCodec] Cannot decode class param: no constructor resolved.');
      }
      decodeObjectFromBinary(reader, instance as object, undefined, '', new Map());
      return instance;
    }

    case 'array': {
      const count = reader.readUint16();
      const result: unknown[] = [];
      const existingArr = Array.isArray(currentValue) ? currentValue : [];
      if (net.element) {
        for (let i = 0; i < count; i++) {
          result.push(decodeRpcValue(reader, existingArr[i] ?? null, net.element));
        }
      }
      return result;
    }

    default:
      console.warn(`[RpcBinaryCodec] Unknown netType during decode: ${(net as NetPropertyMetadata).netType}`);
      return currentValue;
  }
}

// ---------------------------------------------------------------------------
// Short string helpers (uint8 length prefix — max 255 bytes)
// ---------------------------------------------------------------------------

function writeShortString(writer: BinaryWriter, str: string): void {
  const bytes = new TextEncoder().encode(str);
  const len = Math.min(bytes.byteLength, 255);
  writer.writeUint8(len);
  for (let i = 0; i < len; i++) {
    writer.writeUint8(bytes[i]);
  }
}

function readShortString(reader: BinaryReader): string {
  const len = reader.readUint8();
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = reader.readUint8();
  }
  return new TextDecoder().decode(bytes);
}
